import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { WatchEntry } from '@/services/historyApi';
import type { MediaDetails, StreamRef } from '@/types/streaming';
import { useHoverPrefetch } from '@/hooks/useHoverPrefetch';
import { runtimeMsFor } from './filterEligibleEntries';

const TMDB_BACKDROP_BASE = 'https://image.tmdb.org/t/p/w500';
const TMDB_POSTER_BASE = 'https://image.tmdb.org/t/p/w342';

interface Props {
  entry: WatchEntry;
  /** Hydrated TMDB metadata; undefined while the fetch is in flight. */
  media: MediaDetails | undefined;
}

function watchUrlForEntry(entry: WatchEntry): string | null {
  if (entry.movieId !== null) return `/watch/movie/${entry.movieId}`;
  if (entry.tvId !== null && entry.seasonNumber !== null && entry.episodeNumber !== null) {
    return `/watch/tv/${entry.tvId}/${entry.seasonNumber}/${entry.episodeNumber}`;
  }
  return null;
}

function streamRefForEntry(entry: WatchEntry): StreamRef | null {
  if (entry.movieId !== null) return { type: 'movie', tmdbId: entry.movieId };
  if (entry.tvId !== null && entry.seasonNumber !== null && entry.episodeNumber !== null) {
    return { type: 'tv', tmdbId: entry.tvId, season: entry.seasonNumber, episode: entry.episodeNumber };
  }
  return null;
}

function imageUrl(media: MediaDetails | undefined): string | null {
  if (!media) return null;
  if (media.backdropPath) return `${TMDB_BACKDROP_BASE}${media.backdropPath}`;
  if (media.posterPath) return `${TMDB_POSTER_BASE}${media.posterPath}`;
  return null;
}

export function ContinueWatchingCard({ entry, media }: Props): JSX.Element | null {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const href = watchUrlForEntry(entry);
  // Hook must run on every render — fall back to a dummy ref when the entry
  // is malformed (we'll short-circuit to null below before any handler can fire).
  const streamRef = streamRefForEntry(entry) ?? { type: 'movie' as const, tmdbId: 0 };
  const prefetchHandlers = useHoverPrefetch(streamRef);
  if (href === null) return null;

  const isTv = entry.tvId !== null;
  const title = media?.title ?? '';
  const img = imageUrl(media);
  const runtimeMs = media ? runtimeMsFor(entry, media) : null;
  const progressFraction = runtimeMs !== null && runtimeMs > 0
    ? Math.min(1, Math.max(0, entry.progress / runtimeMs))
    : null;

  function go(): void {
    navigate(href!);
  }

  const ariaLabel = isTv
    ? t('continueWatching.ariaTv', {
        title: title || t('continueWatching.seriesFallback'),
        season: entry.seasonNumber,
        episode: entry.episodeNumber,
      })
    : t('continueWatching.ariaMovie', { title: title || t('continueWatching.movieFallback') });

  return (
    <button
      type="button"
      className="continue-card"
      onClick={go}
      aria-label={ariaLabel}
      data-testid={`continue-card-${isTv ? 'tv' : 'movie'}-${entry.tvId ?? entry.movieId}`}
      {...prefetchHandlers}
    >
      <div className="continue-card__thumb">
        {media === undefined ? (
          <div className="continue-card__skeleton" aria-hidden="true" />
        ) : img ? (
          <img src={img} alt="" loading="lazy" />
        ) : (
          <div className="continue-card__placeholder" aria-hidden="true" />
        )}
        {isTv && entry.seasonNumber !== null && entry.episodeNumber !== null && (
          <span className="continue-card__badge">
            S{entry.seasonNumber} · E{entry.episodeNumber}
          </span>
        )}
      </div>
      {progressFraction !== null && (
        <div className="continue-card__progress" aria-hidden="true">
          <div
            className="continue-card__progress-fill"
            style={{ width: `${(progressFraction * 100).toFixed(1)}%` }}
          />
        </div>
      )}
      <div className="continue-card__title" title={title}>
        {title || (media === undefined ? ' ' : isTv ? t('continueWatching.tvSeries') : t('continueWatching.movie'))}
      </div>
    </button>
  );
}
